// ABOUTME: News tab content inside the topbar notifications popover.
// ABOUTME: Shows the latest published news article as a clickable preview, with the rest of recent history beneath.

import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { tokens } from "../../../../theme";
import Text from "../../../theme/text/Text";
import Subheading from "../../../theme/text/Subheading";
import MViewNewsArticle from "../../Modals/MCreateNewsArticle/MViewNewsArticle";
import { useGetNewsArchiveQuery } from "../../../../newapi/global/newsRootSlice";
import { useGetRelationNewsArchiveQuery, useMarkRelationNewsSeenMutation } from "../../../../newapi/warehouse/relationNewsSlice";
import { useGetCustomerLogoQuery } from "../../../../newapi/user/userSlice";
import MViewRelationNews from "../../Modals/MCreateRelationNews/MViewRelationNews";
import NewsSourceLogo from "../components/NewsSourceLogo";
import { mergeNewsFeeds, tiptapToText } from "./newsFeedUtils";
import { ENV_TYPES } from "../../Sidebar";
import { ReactComponent as TableEmpty } from "../../../../styles/svg/tableempty.svg";

const gridOptions = {
    export: false,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const formatDate = (raw, locale) => {
    if (!raw) return "";
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(locale || "nl-NL", { day: "numeric", month: "long", year: "numeric" });
};

const groupByYearMonth = (items) => {
    const groups = new Map();
    for (const a of items) {
        const raw = a.publishedAt || a.createdAt;
        const d = raw ? new Date(raw) : null;
        const valid = d && !Number.isNaN(d.getTime());
        const key = valid ? `${d.getFullYear()}-${d.getMonth()}` : "unknown";
        if (!groups.has(key)) {
            groups.set(key, {
                year: valid ? d.getFullYear() : null,
                month: valid ? d.getMonth() : null,
                items: [],
            });
        }
        groups.get(key).items.push(a);
    }
    return Array.from(groups.values());
};

const formatGroupHeader = (year, month, locale, t) => {
    if (year === null) return t("date_unknown") || "Onbekende datum";
    const d = new Date(year, month, 1);
    const label = d.toLocaleDateString(locale, { month: "long", year: "numeric" });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const TopbarNews = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    const envType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = envType === ENV_TYPES[200];

    const { data: archive } = useGetNewsArchiveQuery({ page: 1, size: 3 });
    const { data: warehouseArchive } = useGetRelationNewsArchiveQuery({ page: 1, size: 3 }, { skip: !isRelation });
    const { data: customerLogo } = useGetCustomerLogoQuery(undefined, { skip: !isRelation });
    const fulfilmentLogoUrl = customerLogo?.logoUrl ?? null;

    const items = useMemo(
        () => mergeNewsFeeds(archive?.items || [], isRelation ? (warehouseArchive?.data || []) : []),
        [archive, warehouseArchive, isRelation]
    );
    const latest = items[0] || null;
    const restGroups = useMemo(() => groupByYearMonth(items.slice(1)), [items]);

    const [viewArticle, setViewArticle] = useState(null);
    const [viewWarehouseNews, setViewWarehouseNews] = useState(null);
    const [markSeen] = useMarkRelationNewsSeenMutation();

    const openItem = (item) => {
        if (item.source === "warehouse") {
            setViewWarehouseNews(item);
            if (!item.seen) markSeen(item.id);
        } else {
            setViewArticle(item);
        }
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions,
        });
    }, []);

    const handleViewAll = () => {
        if (isRelation) {
            navigate("/relation/notifications?view=news");
        } else {
            navigate("/warehouse/notifications?view=news");
        }
        props.onClose?.();
    };

    return (
        <Box>
            <Box
                sx={{
                    zIndex: 1,
                    maxHeight: 470,
                    overflow: "auto",
                    background: theme.palette.mode === "dark" ? colors.grey[200] : "#fff",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {items.length === 0 ? (
                    <Box
                        sx={{
                            p: 3,
                            pt: 1,
                            mt: "auto",
                            mb: "auto",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 450,
                        }}
                    >
                        <TableEmpty />
                        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Subheading textAlign="center">{t("news_empty_title") || "Geen nieuws"}</Subheading>
                            <Text align="center" light>{t("no_news_found") || "Er is nog geen nieuws gepubliceerd"}</Text>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, minWidth: 450 }}>
                        {/* Latest article preview */}
                        <ButtonBase
                            onClick={() => openItem(latest)}
                            sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                boxShadow: `0px 1px 2px 1px rgba(49, 65, 95, 0.15)`,
                                display: "flex",
                                flexDirection: "column",
                                textAlign: "left",
                                width: "100%",
                                transition: "150ms",
                                alignItems:'flex-start',
                                "&:hover": {
                                    boxShadow: `0px 4px 12px rgba(49, 65, 95, 0.2)`,
                                    transform: "translateY(-1px)",
                                },
                            }}
                        >
                            {latest?.source !== "warehouse" && latest?.headerImage?.url && (
                                <Box
                                    component="img"
                                    src={latest.headerImage.url}
                                    alt=""
                                    sx={{
                                        width: "100%",
                                        height: 140,
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            )}
                            <Box
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <NewsSourceLogo source={latest?.source} logoUrl={fulfilmentLogoUrl} size={20} />
                                    <Text bold fontSize={15}>
                                        {latest?.title || t("untitled") || "Zonder titel"}
                                    </Text>
                                </Box>
                                <Text light fontSize={12}>
                                    {formatDate(latest?.publishedAt, i18n.language)}
                                </Text>
                                {latest?.source === "warehouse" && latest?.body && (
                                    <Text light fontSize={13} sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                        {tiptapToText(latest.body)}
                                    </Text>
                                )}
                            </Box>
                        </ButtonBase>

                        {/* Rest of recent history — grouped by month/year */}
                        {restGroups.map((g) => (
                            <Box key={`${g.year}-${g.month}`} sx={{ display: "flex", flexDirection: "column" }}>
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: colors.txt["secondary"],
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                    }}
                                >
                                    {formatGroupHeader(g.year, g.month, i18n.language, t)}
                                </Box>
                                {g.items.map((a) => (
                            <ButtonBase
                                key={a.id}
                                onClick={() => openItem(a)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    p: 1,
                                    borderRadius: 2,
                                    textAlign: "left",
                                    width: "100%",
                                    "&:hover": {
                                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                    },
                                }}
                            >
                                <NewsSourceLogo source={a.source} logoUrl={fulfilmentLogoUrl} size={22} />
                                {a.headerImage?.url && (
                                    <Box
                                        component="img"
                                        src={a.headerImage.url}
                                        alt=""
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            objectFit: "cover",
                                            borderRadius: 1.5,
                                            display: "block",
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                                    <Text bold fontSize={13} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {a.title || t("untitled") || "Zonder titel"}
                                    </Text>
                                    <Text light fontSize={11}>
                                        {formatDate(a.publishedAt)}
                                    </Text>
                                </Box>
                            </ButtonBase>
                                ))}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {items.length > 0 && (
                <Box
                    sx={{
                        p: 2,
                        py: 1.5,
                        display: "flex",
                        justifyContent: "flex-end",
                        borderTop: `1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? null : `0px 0px 8px ${colors.grey[200]}`,
                    }}
                >
                    <ButtonBase
                        onClick={handleViewAll}
                        sx={{
                            width: 1,
                            height: 32,
                            borderRadius: 5,
                            px: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                            color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                            fontSize: 14,
                            fontWeight: 700,
                            fontFamily: "Roboto, sans-serif",
                        }}
                    >
                        {t("view_all")}
                    </ButtonBase>
                </Box>
            )}

            <MViewNewsArticle
                open={Boolean(viewArticle)}
                handleClose={() => setViewArticle(null)}
                article={viewArticle}
            />
            <MViewRelationNews
                open={Boolean(viewWarehouseNews)}
                handleClose={() => setViewWarehouseNews(null)}
                newsItem={viewWarehouseNews}
                logoUrl={fulfilmentLogoUrl}
            />
        </Box>
    );
};

export default TopbarNews;
